import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SortableTree from "react-sortable-tree";
import TreeTheme from "react-sortable-tree-theme-minimal";
import "react-sortable-tree/style.css";
import Swal from "sweetalert2";
import Badge from "../../../library/Badge";
import Banner from "../../../library/Banner";
import Button from "../../../library/Button";
import DropdownButton from "../../../library/DropdownButton";
import StickyFooter from "../../../library/StickyFooter";
import { exportTableData } from "../../../../utils/commonFunction";
import { DeleteProgrammeStudyPath, ReArrangeStudyPathPhase } from "../../../../services/ProgramService";
import { invalidateStudyPathQueries, useStudyPathPhaseList } from "../../../../query/hooks/QueryHooksStudyPaths";
import { buildPhaseExportRows, PHASE_EXPORT_HEADERS } from "./studyPaths.utils";
import PhaseRearrangeRow from "./PhaseRearrangeRow";
import useStudyPathPermissions from "./useStudyPathPermissions";
import StudyPhasesToggle from "./StudyPhasesToggle";
import {
    StyledLockedBannerWrap,
    StyledRearrangeRow,
    StyledRearrangeTree,
    StyledToolbarActions,
    StyledToolbarRow,
} from "./studyPaths.styles";

/**
 * Drag phases into a new order, then Save or Cancel. Reuses the legacy `StudyPathRearrange.js`'s
 * ordering/saving/cancelling logic (`react-sortable-tree`) — only each row's and the action bar's
 * appearance differs.
 * @param {string | number} props.id PMC programme id.
 * @param {object} [props.versionData] Programme version record, for its `lock_version_settings`.
 */
function RearrangePhases({ id: programId, versionData }) {
    const history = useHistory();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const queryVersionId = query.get("versionId");
    const queryProgrammeId = query.get("programmeId") || programId;

    const isLocked = Boolean(versionData?.lock_version_settings);
    const disabledReason = isLocked ? "Unlock the version to make changes." : undefined;

    const { canAdd, canEdit, canDelete, noPermissionReason } = useStudyPathPermissions();
    const reasonFor = (granted) => (isLocked ? disabledReason : (granted ? undefined : noPermissionReason));

    const listRequestData = useMemo(() => ({
        programme_id: queryProgrammeId,
        version_id: queryVersionId,
        page: 1,
        limit: 100,
        key: "order_number",
        sort: "ASC",
    }), [queryProgrammeId, queryVersionId]);

    const { data: phaseListData, isLoading } = useStudyPathPhaseList(listRequestData);
    const [treeData, setTreeData] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setTreeData(phaseListData.data.map((phase) => ({ phase, expanded: true })));
    }, [phaseListData.data]);

    useEffect(() => {
        if (!isLoading && phaseListData.data.length === 0) {
            history.replace(`/program-management-center/programmes/versions/study_path?type=table&versionId=${queryVersionId}&programmeId=${queryProgrammeId}`);
        }
    }, [isLoading, phaseListData.data, history, queryVersionId, queryProgrammeId]);

    const goTo = (type, extra = "") => {
        history.push(`/program-management-center/programmes/versions/study_path?type=${type}&versionId=${queryVersionId}&programmeId=${queryProgrammeId}${extra}`);
    };

    const handleDelete = (phase) => {
        Swal.fire({
            title: "Are you sure?",
            text: `Delete phase "${phase.name}"? This cannot be undone.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            customClass: { popup: "ami-confirm-dialog" },
        }).then(async (result) => {
            if (!result.isConfirmed) {
                return;
            }
            try {
                await DeleteProgrammeStudyPath({ id: phase.id });
                Swal.fire("Deleted!", "The phase has been removed.", "success");
                await invalidateStudyPathQueries(queryProgrammeId, queryVersionId, phase.id);
            } catch (error) {
                console.log("RearrangePhases.js->handleDelete->Error", error);
                Swal.fire("Error", error?.response?.data?.message || "Something went wrong.", "error");
            }
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const studyPathIds = treeData.map((node) => node.phase.id);
            await ReArrangeStudyPathPhase({ study_paths: studyPathIds, version_id: queryVersionId });
            Swal.fire("Saved!", "The new phase order has been saved.", "success");
            await invalidateStudyPathQueries(queryProgrammeId, queryVersionId);
        } catch (error) {
            console.log("RearrangePhases.js->handleSave->Error", error);
            Swal.fire("Error", error?.response?.data?.message || "Something went wrong.", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setTreeData(phaseListData.data.map((phase) => ({ phase, expanded: true })));
    };

    const handleExport = (fileType) => {
        const rows = buildPhaseExportRows(phaseListData.data);
        exportTableData(fileType, "Study Phases", PHASE_EXPORT_HEADERS, rows);
    };

    return (
        <div>
            {isLocked && (
                <StyledLockedBannerWrap>
                    <Banner tone="locked" badge={<Badge tone="warningSolid" icon="lock">Locked</Badge>}>
                        Study path phases are currently read-only. Creating, editing, re-arranging and
                        module assignment are disabled. Unlock the version to make changes.
                    </Banner>
                </StyledLockedBannerWrap>
            )}

            <StyledToolbarRow>
                <StudyPhasesToggle
                    mode="re-arrange"
                    onChange={(mode) => goTo(mode)}
                    rearrangeDisabled={!isLoading && phaseListData.data.length === 0}
                    rearrangeDisabledReason="There aren't any study phases yet."
                />
                <StyledToolbarActions>
                    <DropdownButton
                        icon="download"
                        label="Export"
                        options={[
                            { key: "xlsx", icon: "file-excel", label: "Spreadsheet", onClick: () => handleExport("xlsx") },
                            { key: "csv", icon: "file-csv", label: "CSV", onClick: () => handleExport("csv") },
                            { key: "pdf", icon: "file-pdf", label: "PDF", onClick: () => handleExport("pdf") },
                        ]}
                    />
                    <Button
                        type="button"
                        onClick={() => goTo("create")}
                        disabled={isLocked || !canAdd}
                        title={reasonFor(canAdd)}
                    >
                        <i className="fal fa-plus" aria-hidden="true" /> Create New Phase
                    </Button>
                </StyledToolbarActions>
            </StyledToolbarRow>

            {!isLoading && (
                <StyledRearrangeTree style={{ height: Math.max(240, treeData.length * 96) }}>
                    {/* rowHeight=67 leaves the spacing-2-5 (10px) gap Figma specifies below each ~57px row. */}
                    <SortableTree
                        treeData={treeData}
                        onChange={setTreeData}
                        theme={TreeTheme}
                        isVirtualized={false}
                        rowHeight={67}
                        maxDepth={1}
                        canDrag={!isLocked}
                        canNodeHaveChildren={() => false}
                        generateNodeProps={({ node }) => ({
                            title: (
                                <StyledRearrangeRow>
                                    <PhaseRearrangeRow
                                        phase={node.phase}
                                        onEdit={() => goTo("open", `&id=${node.phase.id}`)}
                                        onDelete={() => handleDelete(node.phase)}
                                        editDisabled={isLocked || !canEdit}
                                        editDisabledReason={reasonFor(canEdit)}
                                        deleteDisabled={isLocked || !canDelete}
                                        deleteDisabledReason={reasonFor(canDelete)}
                                    />
                                </StyledRearrangeRow>
                            ),
                        })}
                    />
                </StyledRearrangeTree>
            )}

            <StickyFooter
                onSave={handleSave}
                onCancel={handleCancel}
                isLoading={isSaving}
                saveDisabled={isLocked || !canEdit}
                cancelDisabled={isSaving}
            />
        </div>
    );
}

RearrangePhases.displayName = "RearrangePhases";

export default RearrangePhases;
